import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Download, AlertTriangle, Send, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import customerApi from "./customerApi";

const AUBERGINE  = "var(--hq-deep, #5A4565)";
const PLUM       = "var(--hq-accent, #6F4A7A)";
const PARCH_EDGE = "#EFE4CC";
const GOLD = "#C9A268";

const STAGES = ["awaiting_payment","awaiting_inventory","assigned","finalised","shipping","quality_control","completed"];
const STAGE_ALIASES = { delivery: "shipping", delivered: "quality_control" };

export default function HQOrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showComplaint, setShowComplaint] = useState(false);
  const [complaint, setComplaint] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    customerApi.get("/customer/orders").then((r) => {
      const all = [].concat(r.data?.console || [], r.data?.online || [], r.data?.manual || []);
      const found = all.find((o) => o.id === orderId || o.order_number === orderId);
      setOrder(found || null);
    }).finally(() => setLoading(false));
  }, [orderId]);

  const submitComplaint = async () => {
    if (!complaint.trim()) return;
    setSending(true);
    try {
      await customerApi.post(`/customer/orders/${order.id}/note`, { note: `[Concern raised] ${complaint.trim()}` });
      toast.success("Sent. Kayla will be in touch.");
      setComplaint("");
      setShowComplaint(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send. Try the Contact tab.");
    } finally { setSending(false); }
  };

  if (loading) return <div className="opacity-70"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>;
  if (!order) return (
    <div data-testid="hq-order-detail-missing" style={{ color: AUBERGINE }}>
      <Link to="/account/orders" className="text-xs tracking-[0.25em] uppercase inline-flex items-center gap-1.5"><ArrowLeft className="w-3 h-3" /> Back</Link>
      <div className="mt-4 italic opacity-70">Order not found, it may have been removed or moved to archive.</div>
    </div>
  );

  const stageIdx = STAGES.indexOf(STAGE_ALIASES[order.stage] || order.stage);
  const lines = order.line_items || order.items || [];

  return (
    <div data-testid="hq-order-detail" className="space-y-5" style={{ color: AUBERGINE }}>
      <Link to="/account/orders" data-testid="hq-order-back" className="text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ color: PLUM }}>
        <ArrowLeft className="w-3 h-3" /> All orders
      </Link>

      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Order</div>
        <h1 className="text-3xl mt-1">{order.order_number ? `#${order.order_number}` : `Ref ${String(order.id || "").slice(0, 8)}`}</h1>
        <div className="text-xs opacity-70 mt-1">Placed {String(order.created_at || "").slice(0, 10)}</div>
      </header>

      {/* Status timeline */}
      <section className="bg-white border rounded p-4" style={{ borderColor: PARCH_EDGE }} data-testid="hq-order-timeline">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-3" style={{ color: PLUM }}>Status</div>
        <ol className="grid grid-cols-7 gap-1 text-[9px] tracking-[0.2em] uppercase">
          {STAGES.map((s, i) => (
            <li key={s} className="text-center">
              <div className="h-1 w-full" style={{ backgroundColor: i <= stageIdx ? GOLD : "#E0D1C8" }} />
              <div className="mt-1.5 opacity-80 leading-tight">{s.replace(/_/g, " ")}</div>
            </li>
          ))}
        </ol>
      </section>

      {/* Line items */}
      {lines.length > 0 && (
        <section className="bg-white border rounded p-4" style={{ borderColor: PARCH_EDGE }} data-testid="hq-order-items">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-2" style={{ color: PLUM }}>Items</div>
          <ul className="text-sm space-y-1">
            {lines.map((li, i) => (
              <li key={i} className="flex justify-between gap-3">
                <span>{li.qty || li.quantity || 1} × {li.name}</span>
                <span className="opacity-70">NZ${Number(li.unit_price || li.price || 0).toFixed(2)}</span>
              </li>
            ))}
          </ul>
          <div className="border-t mt-3 pt-2 flex justify-between text-sm font-semibold" style={{ borderColor: PARCH_EDGE }}>
            <span>Total</span><span>NZ${Number(order.total || 0).toFixed(2)}</span>
          </div>
        </section>
      )}

      {/* Tracking / local delivery */}
      {order.tracking_number ? (
        <section className="bg-white border rounded p-4" style={{ borderColor: PARCH_EDGE }} data-testid="hq-order-tracking">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-1" style={{ color: PLUM }}>Shipped , tracking</div>
          <div className="text-sm">{order.shipping_provider || "NZ Post"} · <strong>{order.tracking_number}</strong></div>
          <a
            href={`https://www.nzpost.co.nz/tools/tracking/item/${encodeURIComponent(order.tracking_number)}`}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="hq-order-track-nzpost"
            className="mt-3 inline-flex h-11 px-4 items-center rounded text-[10px] tracking-[0.25em] uppercase font-semibold text-white"
            style={{ backgroundColor: "#6E1E3A" }}
          >
            Track with NZ Post →
          </a>
        </section>
      ) : ["shipping", "delivery"].includes(order.stage) ? (
        <section className="bg-white border rounded p-4" style={{ borderColor: PARCH_EDGE }} data-testid="hq-order-local-delivery">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-1" style={{ color: PLUM }}>Out for local delivery</div>
          <div className="text-sm italic opacity-80">
            No tracking on this one , Heritage is hand delivering it on the local run.
          </div>
        </section>
      ) : null}

      {/* HQ picks note (if any) */}
      {(order.hq_picks_note || order.mystery_notes) && (
        <section className="bg-white border rounded p-4" style={{ borderColor: PARCH_EDGE }} data-testid="hq-order-picks">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-1" style={{ color: PLUM }}>Your box picks</div>
          <pre className="whitespace-pre-wrap text-sm italic font-serif">
            {order.hq_picks_note ||
              (typeof order.mystery_notes === "string"
                ? order.mystery_notes
                : Object.values(order.mystery_notes || {}).filter((v) => typeof v === "string").join("\n"))}
          </pre>
        </section>
      )}

      {/* Payment due , bank transfer only (invoice). Shown whenever unpaid. */}
      {order.payment_status !== "paid" && !order.paid && Number(order.total || 0) > 0 && (
        <section className="bg-white border-2 rounded p-4" style={{ borderColor: GOLD }} data-testid="hq-order-pay-panel">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>
            Pay by bank transfer · NZ${Number(order.total || 0).toFixed(2)}
          </div>
          <div className="mt-3 space-y-2 text-sm">
            <PayRow label="Bank" value="Kiwi Bank" />
            <PayRow label="Account name" value="HERITAGE DAHLIAS NZ LIMITED" />
            <PayRow label="Account number" value="38-9027-0833898-00" copy testid="hq-pay-copy-account" />
            <PayRow label="Reference" value={(order.id || "").slice(0, 8).toUpperCase()} copy testid="hq-pay-copy-reference" />
          </div>
          <p className="text-xs opacity-70 mt-3 leading-relaxed">
            Please pay within 48 hours to secure your order , reserved tubers and boxes are
            released back into stock if payment doesn&apos;t clear in time.
          </p>
        </section>
      )}

      {/* Actions */}
      <section className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={async () => {
            try {
              const r = await customerApi.get(`/customer/orders/${order.id}/invoice/html`, { responseType: "text" });
              const blob = new Blob([r.data], { type: "text/html" });
              window.open(URL.createObjectURL(blob), "_blank", "noopener");
            } catch (e) {
              toast.error(e?.response?.status === 404
                ? "No invoice on file for this order yet , give us a nudge and we'll add one."
                : "Couldn't open the invoice just now , please try again.");
            }
          }}
          data-testid="hq-order-invoice-link"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-[10px] tracking-[0.25em] uppercase font-semibold border"
          style={{ borderColor: AUBERGINE, color: AUBERGINE }}
        >
          <Download className="w-3 h-3" /> {(order.paid || order.payment_status === "paid") ? "Download receipt" : "View invoice"}
        </button>
        <button
          type="button"
          data-testid="hq-order-complaint-btn"
          onClick={() => setShowComplaint(!showComplaint)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-[10px] tracking-[0.25em] uppercase font-semibold"
          style={{ backgroundColor: "#A33030", color: "white" }}
        >
          <AlertTriangle className="w-3 h-3" /> Raise a concern
        </button>
      </section>

      {showComplaint && (
        <section className="bg-white border rounded p-4 space-y-2" style={{ borderColor: PARCH_EDGE }} data-testid="hq-order-complaint-form">
          <textarea
            value={complaint}
            onChange={(e) => setComplaint(e.target.value)}
            rows={4}
            data-testid="hq-order-complaint-text"
            placeholder="Tell us what's wrong, we'll respond within a few days."
            className="w-full px-3 py-2 text-sm border rounded outline-none"
            style={{ borderColor: PARCH_EDGE }}
          />
          <button
            type="button"
            disabled={sending || !complaint.trim()}
            onClick={submitComplaint}
            data-testid="hq-order-complaint-send"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-xs tracking-[0.2em] uppercase font-semibold"
            style={{ backgroundColor: AUBERGINE, color: "#FBF6E9" }}
          >
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Send concern
          </button>
        </section>
      )}
    </div>
  );
}

function PayRow({ label, value, copy = false, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-xs opacity-60 uppercase tracking-[0.15em]">{label}</span>
      <span className="inline-flex items-center gap-2 font-semibold">
        {value}
        {copy && (
          <button
            type="button"
            data-testid={testid}
            onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copied.`); }}
            className="opacity-60 hover:opacity-100"
            title={`Copy ${label.toLowerCase()}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}
